import { Box, Pagination, Typography } from '@mui/material';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { gql } from '@apollo/client';
import Banner from '@components/molecules/Banner';
import Container from '@components/atoms/Container';
import PageInsetTop from '@components/atoms/PageInsetTop';
import DefaultTemplate from '@components/templates/DefaultTemplate';
import BlogPreviewItem from '@components/molecules/BlogPreviewItem';
import { getAppData } from '@lib/appInitialState';
import { useTranslation } from 'next-i18next';

export default function Blog({
  appData,
  blogItems,
  currentPage,
  pageCount,
}) {
  const { t } = useTranslation();
  const router = useRouter();

  const handleChangePage = (event, page) => {
    router.push(`/blog?p=${page}`);
  };

  return (
    <>
      <Head>
        <title>
          {t('pages.blog.home.pageTitle')}
        </title>
        <meta
          content={t('pages.blog.home.metaDescription')}
          name="description"
        />
        <meta content={t('pages.blog.home.pageTitle')} property="og:title" />
      </Head>
      <DefaultTemplate>
        <PageInsetTop />
        <Container>
          <Banner appData={appData} />
          <Box mt={3}>
            <Typography variant="h1">
              Schimmel News
            </Typography>
          </Box>
          <Box mt={5}>
            {blogItems.map((item) => (
              <Box key={item.id} mb={5}>
                <BlogPreviewItem item={item} />
              </Box>
            ))}
          </Box>
          <Box justifyContent="center" mb={10} mt={5} sx={{ display: { xs: 'flex', md: 'none' } }}>
            <Pagination
              boundaryCount={1}
              count={pageCount}
              onChange={handleChangePage}
              page={currentPage}
              siblingCount={0}
            />
          </Box>
          <Box justifyContent="center" mb={10} mt={5} sx={{ display: { xs: 'none', md: 'flex' } }}>
            <Pagination
              count={pageCount}
              onChange={handleChangePage}
              page={currentPage}
            />
          </Box>
        </Container>
      </DefaultTemplate>
    </>
  );
}

Blog.propTypes = {};

export async function getServerSideProps(ctx) {
  const {
    query,
    req,
    res,
    locale,
  } = ctx;
  const [graphQlClient, appData] = await getAppData(ctx);
  const { p } = query;
  let page = parseInt(p, 10);

  if (Number.isNaN(page) || page < 1) {
    page = 1;
  }

  const start = (page - 1) * ITEMS_PER_PAGE;
  const limit = ITEMS_PER_PAGE;

  const {
    error,
    data,
  } = await graphQlClient.query({
    query: FETCH_BLOG_ITEMS,
    variables: {
      start,
      limit,
      locale,
    },
  });

  if (error) {
    throw error;
  }

  const redirectUrl = data.blogOverviewPage?.redirectUrl?.trim();

  if (data.blogOverviewPage?.publishedAt && redirectUrl) {
    let canRedirect = false;
    try {
      const dest = new URL(redirectUrl);
      const currentHost = req?.headers?.host;
      const currentPath = (req?.url ?? '').split('?')[0];
      canRedirect = !(dest.host === currentHost && dest.pathname === currentPath);
    } catch {
      canRedirect = false;
    }
    if (canRedirect) {
      return {
        redirect: {
          destination: redirectUrl,
          statusCode: 301,
        },
      };
    }
  }

  if (res) {
    res.setHeader('Cache-Control', 'public, max-age=3600');
  }

  return {
    props: {
      ...appData,
      blogItems: data.pages.data,
      currentPage: page,
      // pageCount: Math.ceil(data.pagesConnection.aggregate.count / ITEMS_PER_PAGE),
      pageCount: data.pages.meta.pagination.pageCount,
    },
  };
}

const ITEMS_PER_PAGE = 5;

const FETCH_BLOG_ITEMS = gql`
  query GetBlogItems($start: Int!, $limit: Int!, $locale: I18NLocaleCode!) {
    blogOverviewPage: resolvePageByPath(path: "blog", locale: $locale) {
      publishedAt
      redirectUrl
    }
    pages(
      filters: {
        publishedAt: { null: false },
        pageType: { in: ["blogItem", "campaignItem"] }
      },
      locale: $locale,
      sort: [ "isPinnedToTop:desc", "publishDate:desc", "publishedAt:desc" ],
      pagination: {
        start: $start,
        limit: $limit
      }
    ) {
      data {
        ${BlogPreviewItem.graphQlSchema}
      }
      meta {
        pagination {
          total
          page
          pageSize
          pageCount
        }
      }
    }
  }
`;
